"use client";

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { cn } from '@/lib/utils';

const REVIEWS = [
  { 
    quote: "I was putting this off for years because I thought it would be complicated. It took me literally 15 minutes on a Sunday morning.", 
    author: "James M.", 
    role: "Manchester" 
  },
  { 
    quote: "Simple, clear, and legally sound. My solicitor friend looked it over and said it was solid. Saved me hundreds.", 
    author: "Eleanor R.", 
    role: "Bristol" 
  },
  { 
    quote: "The step-by-step guidance made it impossible to get wrong. Highly recommended for anyone wanting peace of mind.", 
    author: "David K.", 
    role: "London" 
  },
  {
    quote: "The process was so much easier than I expected. I feel so relieved having this sorted for my kids.",
    author: "Sarah T.",
    role: "Leeds"
  },
  {
    quote: "I wasn't sure about the wording for a specific gift. Being able to chat with a real human and get it sorted in minutes was a game changer.",
    author: "Thomas H.",
    role: "Liverpool"
  },
  {
    quote: "Excellent service. I had a quick question about my executors and the support team explained it all clearly. Feels good to have it done properly.",
    author: "Rebecca W.",
    role: "Newcastle"
  },
  {
    quote: "Great value for money. Solicitors were quoting me £200+ for the same thing.",
    author: "Mike B.",
    role: "Birmingham"
  }
];

export const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState<number>(2); // Start with middle one active

  return (
    <section className="py-24 border-y border-foreground/10 bg-background overflow-hidden">
      <div className="container mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-4xl md:text-5xl mb-4 text-teal">Trusted by families across the UK</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Join those who have secured their legacy with our platform.
          </p>
        </motion.div>
        
        {/* Interconnected Accordion */}
        <div className="flex flex-col md:flex-row h-[800px] md:h-[600px] w-full mx-auto items-stretch justify-center gap-0 border border-foreground/10 rounded-xl overflow-hidden shadow-sm bg-white">
            {REVIEWS.map((review, i) => {
                const isActive = activeIndex === i;
                
                return (
                    <motion.div 
                        key={i}
                        className={cn(
                            "relative border-b md:border-b-0 md:border-r border-foreground/10 last:border-0 overflow-hidden cursor-pointer group transition-colors duration-500",
                            isActive ? "bg-white" : "bg-secondary/5 hover:bg-secondary/10"
                        )}
                        initial={false}
                        animate={{ 
                            flexGrow: isActive ? 3 : 1,
                            flexBasis: isActive ? "40%" : "15%"
                        }}
                        transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
                        onMouseEnter={() => setActiveIndex(i)}
                        onClick={() => setActiveIndex(i)}
                    >
                        {/* Connecting Line (Desktop) */}
                        <div className="hidden md:block absolute top-12 left-0 right-0 h-px bg-foreground/10 group-hover:bg-teal/30 transition-colors z-0" />
                        
                        {/* Connecting Line (Mobile) */}
                        <div className="md:hidden absolute top-0 bottom-0 left-12 w-px bg-foreground/10 group-hover:bg-teal/30 transition-colors z-0" />

                        <div className="relative z-10 h-full flex md:flex-col p-6 md:p-8">
                            
                            {/* Header / Avatar Node */}
                            <div className={cn(
                                "flex md:flex-col items-center md:items-start gap-4 md:gap-0 md:min-w-0 transition-all duration-300",
                                isActive ? "min-w-0" : "min-w-[150px] md:min-w-0"
                            )}>
                                {/* Node on the line */}
                                <div className={cn(
                                    "w-12 h-12 rounded-full border-2 flex items-center justify-center text-lg font-display transition-all duration-300 md:mb-6 shrink-0 relative",
                                    isActive 
                                        ? "bg-teal text-white border-teal scale-110 shadow-lg" 
                                        : "bg-white text-foreground border-foreground/20 group-hover:border-teal/50"
                                )}>
                                    {review.author[0]}
                                    {/* Pulse effect when active */}
                                    {isActive && (
                                        <div className="absolute inset-0 rounded-full bg-teal animate-ping opacity-20" />
                                    )}
                                </div>

                                {/* Compact Info (Visible when collapsed) */}
                                <motion.div 
                                    className="flex flex-col justify-center"
                                    animate={{ 
                                        opacity: isActive ? 0 : 1,
                                        y: isActive ? -10 : 0,
                                        height: isActive ? 0 : 'auto'
                                    }}
                                    transition={{ duration: 0.3 }}
                                >
                                    <div className="font-bold text-sm truncate max-w-[120px]">{review.author}</div>
                                    <div className="text-xs text-muted-foreground truncate">{review.role}</div>
                                </motion.div>
                            </div>

                            {/* Expanded Content */}
                            <motion.div 
                                className="flex-1 flex flex-col justify-center min-w-0"
                                initial={{ opacity: 0 }}
                                animate={{ 
                                    opacity: isActive ? 1 : 0,
                                    x: isActive ? 0 : 20 
                                }}
                                transition={{ duration: 0.4, delay: 0.1 }}
                            >
                                {isActive && (
                                    <>
                                        <div className="mb-6 flex gap-1">
                                            {[1,2,3,4,5].map(star => (
                                                <Star key={star} className="w-5 h-5 text-orange-400 fill-orange-400" />
                                            ))}
                                        </div>
                                        
                                        <p className="text-lg md:text-xl font-light leading-relaxed italic text-foreground/90 mb-6 line-clamp-4 md:line-clamp-none">
                                            {review.quote}
                                        </p>
                                        
                                        <div className="mt-auto">
                                            <div className="font-bold text-lg">{review.author}</div>
                                            <div className="text-sm text-teal font-medium">{review.role}</div>
                                        </div>
                                    </>
                                )}
                            </motion.div>

                            {/* Vertical Text for Collapsed State (Desktop only) */}
                            {!isActive && (
                                <div className="hidden md:block absolute bottom-8 left-8 right-8 text-center">
                                    {/* Optional: Add something here if needed, but clean is better */}
                                </div>
                            )}
                        </div>
                    </motion.div>
                );
            })}
        </div>
      </div>
    </section>
  );
};
